// T085: Create delete confirmation dialog

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDeletePost } from '@/hooks/usePost'
import { Loader2, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import type { Post } from '@/types/post'

interface DeletePostDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  post: Post
  onDeleteSuccess?: () => void
}

export function DeletePostDialog({ open, onOpenChange, post, onDeleteSuccess }: DeletePostDialogProps) {
  const deletePost = useDeletePost()

  const handleDelete = async () => {
    try {
      await deletePost.mutateAsync({
        permlink: post.permlink,
      })

      toast.success('Post deleted successfully')
      onOpenChange(false)
      onDeleteSuccess?.()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete post'
      toast.error('Failed to delete post', {
        description: errorMessage,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Post
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this post? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border p-4">
          <h4 className="font-medium">{post.title}</h4>
          <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
            {post.summary || post.body.substring(0, 200)}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deletePost.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deletePost.isPending}
          >
            {deletePost.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete Post'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
